import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, Wrench, Shield, Clock, Star, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { CallButton } from './CallButton';
import { TopAnnouncementBar } from './TopAnnouncementBar';

export const LandRoverRepair = () => {
  const landroverServices = [
    {
      title: "Engine Diagnostics & Repair",
      description: "Advanced Land Rover engine diagnostics using specialized tools for accurate problem detection",
      icon: Wrench
    },
    {
      title: "Terrain Response Service",
      description: "Expert Land Rover 4WD and Terrain Response system repair and maintenance",
      icon: Wrench
    },
    {
      title: "Brake System Service",
      description: "Complete Land Rover brake inspection, pad replacement, and rotor servicing",
      icon: Shield
    },
    {
      title: "Electrical Systems",
      description: "Land Rover electrical diagnostics and repair for all electronic components",
      icon: Wrench
    },
    {
      title: "Suspension & Steering",
      description: "Land Rover air suspension repair and wheel alignment for optimal handling",
      icon: Wrench
    },
    {
      title: "Oil Change & Maintenance",
      description: "Land Rover-specific oil changes and routine maintenance services",
      icon: Clock
    },
    {
      title: "AC System Repair",
      description: "Land Rover air conditioning service and refrigerant recharge",
      icon: Wrench
    },
    {
      title: "Battery & Alternator",
      description: "Land Rover battery replacement and charging system diagnostics",
      icon: Wrench
    }
  ];

  const landroverModels = [
    "Land Rover Defender", "Land Rover Defender 90", "Land Rover Defender 110", "Land Rover Range Rover",
    "Range Rover Sport", "Range Rover Velar", "Range Rover Evoque", "Land Rover Discovery",
    "Land Rover Discovery Sport", "Range Rover Autobiography", "Range Rover SVAutobiography", "Range Rover P400e",
    "Land Rover Freelander", "Land Rover LR2", "Land Rover LR3", "Land Rover LR4"
  ];

  const whyChooseUs = [
    {
      title: "Land Rover-Certified Technicians",
      description: "Our mechanics are specially trained and certified for Land Rover repairs"
    },
    {
      title: "Genuine Parts Available",
      description: "We use OEM and genuine Land Rover parts for all repairs and replacements"
    },
    {
      title: "Advanced Diagnostic Tools",
      description: "State-of-the-art Land Rover diagnostic equipment for precise problem identification"
    },
    {
      title: "Warranty on Services",
      description: "All Land Rover repairs come with comprehensive warranty coverage"
    }
  ];

  const openWhatsApp = () => {
    const message = "Hi Think Mechanic, I need Land Rover repair service and want to claim my 15% website discount!";
    const whatsappNumber = '+971566671468';
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Land Rover Repair Dubai | Range Rover Service & Maintenance | Think Mechanic</title>
        <meta name="description" content="Professional Land Rover and Range Rover repair in Dubai including engine diagnostics, AC repair, brake service, air suspension and electrical repairs at Think Mechanic Ras Al Khor." />
        <link rel="canonical" href="https://thinkmechanicdxb.com/landrover-repair-dubai" />
        <meta name="keywords" content="Land Rover repair Dubai, Range Rover service Dubai, Land Rover garage Dubai, Range Rover workshop Dubai, Land Rover mechanic Dubai, Range Rover specialist Dubai, Land Rover Defender repair, Range Rover Sport service Dubai, Land Rover Discovery repair, Think Mechanic Land Rover" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Land Rover Repair Dubai | Expert Range Rover Service | Think Mechanic" />
        <meta property="og:description" content="Professional Land Rover and Range Rover repair in Dubai. Certified technicians, genuine parts, 15+ years experience. All Land Rover models serviced." />
        <meta property="og:url" content="https://thinkmechanicdxb.com/landrover-repair-dubai" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Land Rover Repair Dubai | Think Mechanic" />
        <meta name="twitter:description" content="Expert Land Rover and Range Rover repair and maintenance in Dubai. Certified technicians, genuine parts." />
      </Helmet>

      {/* Top Announcement Bar */}
      <TopAnnouncementBar />

      {/* Same Header as Homepage */}
      <Header />

      {/* Hero Section with Land Rover Image Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Land Rover Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1677228447083-3245a27daf91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxMYW5kJTIwUm92ZXIlMjBSYW5nZSUyMFJvdmVyJTIwbHV4dXJ5JTIwU1VWfGVufDB8fHx8MTc4NTU4MDU3MXww&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Land Rover Range Rover - Professional Repair Service Dubai - Think Mechanic"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center py-32">
            {/* Land Rover Logo Badge */}
            <div className="inline-flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-md border-2 border-white/20 px-6 py-3 rounded-full mb-6">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center p-1">
                <img 
                  src="https://images.unsplash.com/photo-1597220602515-eb4471175886?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxMYW5kJTIwUm92ZXIlMjBsb2dvJTIwZW1ibGVtfGVufDB8fHx8MTc4NTU4MDczNXww&ixlib=rb-4.1.0&q=85"
                  alt="Land Rover Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-white font-bold text-lg">Land Rover</span>
                <span className="text-white/80 text-xs font-semibold">Repair Specialists</span>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Expert Land Rover Repair in
              <span className="text-red-600"> Dubai</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Specialized Land Rover and Range Rover repair and maintenance services in Ras Al Khor. Trust certified technicians with your British luxury SUV.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={openWhatsApp}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-red-600/50 group"
              >
                Book Land Rover Service & Get 15% OFF
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-white/90 text-sm">✨ Special discount for website visitors</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">15+</div>
                <div className="text-gray-300 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">500+</div>
                <div className="text-gray-300 text-sm">Land Rovers Serviced</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                <div className="text-gray-300 text-sm">Satisfaction</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10"></div>
      </section>

      {/* Land Rover Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive Land Rover Repair Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From routine maintenance to complex repairs, we handle all Land Rover service needs with expertise and precision.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {landroverServices.map((service, index) => (
              <Card key={index} className="bg-white border-gray-200 p-6 hover:border-red-600 transition-all duration-300">
                <div className="bg-red-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Think Mechanic for Land Rover Repair?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-red-600 rounded-full p-2 flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900 font-bold text-xl mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Land Rover Models We Service */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Land Rover Models We Service
            </h2>
            <p className="text-gray-600 text-lg">
              We service all Land Rover and Range Rover models, from classic to latest
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {landroverModels.map((model, index) => (
              <div key={index} className="bg-white border border-gray-200 px-4 py-3 rounded-lg text-center hover:border-red-600 transition-all duration-300">
                <span className="text-gray-900 font-semibold">{model}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600">+ All other Land Rover models</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Service Your Land Rover?
          </h2>
          <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
            Book your Land Rover service today and get 15% off. Our expert technicians are ready to help!
          </p>
          
          <Button
            onClick={openWhatsApp}
            className="bg-white hover:bg-gray-100 text-red-600 px-10 py-6 rounded-lg text-xl font-bold transition-all duration-200 shadow-2xl hover:scale-105"
          >
            Book Now via WhatsApp
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-white">
            <div className="flex items-center space-x-2">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">056 667 1468</span>
            </div>
            <div className="hidden sm:block w-px h-6 bg-white/30"></div>
            <div>
              <span>📍 Ras Al Khor Industrial Area 1, Dubai</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Call Button */}
      <CallButton />
      
      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};